import { cn } from '@/lib/cn';

/** The white card page content sits in: 8px radius, hairline border, card shadow. */
export function Card({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <section className={cn('card p-6', className)} {...props} />;
}

export interface SectionHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

/** 18px semibold title, optional sentence under it, actions on the right. */
export function SectionHeader({ title, description, actions, className }: SectionHeaderProps) {
  return (
    <header className={cn('flex flex-wrap items-start justify-between gap-3 pb-5', className)}>
      <div className="min-w-0">
        <h2 className="text-xl font-semibold text-gray-3">{title}</h2>
        {description ? <p className="mt-1 max-w-3xl text-base text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-3">{actions}</div> : null}
    </header>
  );
}

/** Label and value rows, the way the portal shows Account Settings. */
export function LabelValueList({ className, ...props }: React.HTMLAttributes<HTMLDListElement>) {
  return <dl className={cn('flex flex-col', className)} {...props} />;
}

export function LabelValue({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-x-6 gap-y-1 border-b border-[color:var(--ts-row-border)] py-4 last:border-b-0',
        className,
      )}
    >
      <dt className="w-[180px] shrink-0 text-base text-muted-foreground">{label}</dt>
      <dd className="min-w-0 flex-1 text-base text-gray-3">{children}</dd>
    </div>
  );
}
